import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { History } from "lucide-react";

const fmtD = (iso) => (iso ? iso.split("-").reverse().join(".") : "—");

const COLOR_META = {
  rosu: { label: "Roșu", hex: "#EF4444" },
  galben: { label: "Galben", hex: "#FDE047" },
  albastru: { label: "Albastru", hex: "#3B82F6" },
  verde: { label: "Verde", hex: "#10B981" },
  gri: { label: "Gri", hex: "#6B7280" },
  mov: { label: "Mov", hex: "#A855F7" },
};

const ColorPicker = ({ current, onPick, disabled }) => (
  <div className="flex items-center gap-1">
    {Object.entries(COLOR_META).map(([code, m]) => (
      <button
        key={code}
        type="button"
        disabled={disabled || code === current}
        onClick={() => onPick(code)}
        className={`w-4 h-4 rounded-full border-2 transition-transform hover:scale-125 ${code === current ? "ring-2 ring-offset-1 ring-zinc-400 cursor-default" : "cursor-pointer"} disabled:opacity-60`}
        style={{ background: m.hex, borderColor: m.hex }}
        title={code === current ? `Culoarea actuală: ${m.label}` : `Schimbă pe ${m.label}`}
        data-testid={`reassign-color-${code}`}
      />
    ))}
    <button
      type="button"
      disabled={disabled}
      onClick={() => onPick(null)}
      className="text-[10px] px-1.5 py-0.5 rounded border border-zinc-300 text-zinc-500 hover:bg-zinc-100 cursor-pointer disabled:opacity-60"
      title="Scoate culoarea de pe acest șantier"
      data-testid="reassign-color-none"
    >
      ✕ fără
    </button>
  </div>
);

export const ToolColorHistory = ({ color }) => {
  const { isAdmin } = useAuth();
  const [items, setItems] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    if (!color || !COLOR_META[color]) return;
    api.get(`/tool-sets/${color}/history-detail`).then((r) => setItems(r.data?.items || [])).catch(() => setItems([]));
  }, [color]);

  useEffect(() => {
    setItems(null);
    load();
  }, [color, load]);

  const reassign = async (it, newColor) => {
    const meta = COLOR_META[color];
    const newLabel = newColor ? COLOR_META[newColor].label : "FĂRĂ culoare";
    if (!window.confirm(`Schimbi setul de scule de pe „${it.site}" din ${meta.label} în ${newLabel}? Se aplică pe toate alocările (inclusiv șantiere încheiate).`)) return;
    setSaving(true);
    try {
      const { data } = await api.put("/tool-sets/reassign", {
        project_id: it.project_id || null,
        regie_id: it.regie_id || null,
        old_color: color,
        new_color: newColor,
      });
      toast.success(`Culoare schimbată pe ${data.updated_entries} alocări (${it.site})`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la schimbarea culorii");
    } finally {
      setSaving(false);
    }
  };

  const meta = COLOR_META[color];
  if (!meta) return null;
  return (
    <div className="card-flat p-4 mb-4 border-l-4" style={{ borderLeftColor: meta.hex }} data-testid={`tool-color-history-${color}`}>
      <div className="flex items-center gap-2 mb-2">
        <History size={16} style={{ color: meta.hex }} />
        <h2 className="font-display text-base font-bold">
          Istoric set <span style={{ color: meta.hex }}>{meta.label}</span> — șantiere & persoane
        </h2>
      </div>
      {items === null && <div className="text-xs text-zinc-500">Se încarcă istoricul…</div>}
      {items !== null && items.length === 0 && (
        <div className="text-xs text-zinc-500">Setul {meta.label} nu a fost încă alocat pe niciun șantier.</div>
      )}
      {items !== null && items.length > 0 && (
        <div className="overflow-x-auto border border-zinc-200 rounded">
          <table className="w-full text-xs">
            <thead className="bg-zinc-100 text-zinc-600">
              <tr>
                <th className="text-left px-2 py-1.5 font-semibold">Șantier</th>
                <th className="text-left px-2 py-1.5 font-semibold">Perioada</th>
                <th className="text-right px-2 py-1.5 font-semibold">Zile</th>
                <th className="text-left px-2 py-1.5 font-semibold">Persoane</th>
                {isAdmin && <th className="text-left px-2 py-1.5 font-semibold" title="Corectează culoarea setului dacă a fost aleasă greșit">Corectează culoarea</th>}
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={i} className="border-t border-zinc-100 hover:bg-zinc-50 align-top" data-testid={`tool-history-row-${color}-${i}`}>
                  <td className="px-2 py-1.5 font-semibold whitespace-nowrap">
                    {it.kind === "regie" ? "🟧" : "📁"} {it.site}
                    {it.vans && it.vans.length > 0 && (
                      <div className="text-[10px] text-zinc-400 font-normal">🚐 {it.vans.join(" · ")}</div>
                    )}
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap tabular-nums">
                    {fmtD(it.first_day)}{it.last_day && it.last_day !== it.first_day ? ` → ${fmtD(it.last_day)}` : ""}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{it.entries}</td>
                  <td className="px-2 py-1.5">
                    {(it.persons || []).length === 0 ? <span className="text-zinc-400">—</span> : (
                      <div className="flex flex-wrap gap-1">
                        {it.persons.map((p, j) => (
                          <span key={j} className="px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-700 whitespace-nowrap">{p}</span>
                        ))}
                      </div>
                    )}
                  </td>
                  {isAdmin && (
                    <td className="px-2 py-1.5">
                      {(it.project_id || it.regie_id) ? (
                        <ColorPicker current={color} disabled={saving} onPick={(nc) => reassign(it, nc)} />
                      ) : <span className="text-zinc-400">—</span>}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {isAdmin && items !== null && items.length > 0 && (
        <div className="text-[10px] text-zinc-400 mt-1.5">Click pe o bulină pentru a corecta culoarea setului pe acel șantier (funcționează și pentru șantiere încheiate).</div>
      )}
    </div>
  );
};
